import {
  formatCurrencyValue,
  formatDateValue,
  formatDocumentLabel,
  formatFieldValue,
  getPresenceStatusMeta,
  resolveEmployeeProfileStatus,
  formatStatusLabel,
  formatWorkingHoursValue,
  getEmployeeDocuments,
  getInitials,
  toAbsoluteUrl,
} from "../utils/employeeHelpers";

const PROFILE_TABS = [
  { key: "personal", label: "Personal Information" },
  { key: "work", label: "Work Information" },
  { key: "salary", label: "Salary Information" },
  { key: "bank", label: "Bank Information" },
  { key: "address", label: "Address Information" },
];

const PROFILE_FIELDS = {
  personal: [
    { label: "Full Name", valueKey: "fullName", icon: "user" },
    { label: "Username", valueKey: "username", icon: "atSign" },
    { label: "Email Address", valueKey: "email", icon: "mail" },
    { label: "Phone Number", valueKey: "phoneNumber", icon: "phone" },
    { label: "Date of Birth", valueKey: "dateOfBirth", icon: "calendar" },
    { label: "Gender", valueKey: "gender", icon: "userCircle" },
    { label: "Marital Status", valueKey: "maritalStatus", icon: "heart" },
    { label: "Status", valueKey: "status", icon: "shield", formatter: formatStatusLabel },
  ],
  work: [
    { label: "Employee ID", valueKey: "employeeId", icon: "badge" },
    { label: "Department", valueKey: "department", icon: "building" },
    { label: "Designation", valueKey: "designation", icon: "briefcase" },
    { label: "Role", valueKey: "role", icon: "userCog" },
    { label: "Employment Type", valueKey: "employmentType", icon: "layers" },
    { label: "Date of Joining", valueKey: "dateOfJoining", icon: "calendar" },
    {
      label: "Working Hours",
      valueKey: "workingHoursLabel",
      icon: "clock",
      formatter: (_, employee) =>
        formatWorkingHoursValue(
          employee.workShiftStart,
          employee.workShiftEnd,
          employee.workingHoursLabel
        ),
    },
    { label: "Reporting Manager", valueKey: "reportingManager", icon: "users" },
    { label: "Last Login", valueKey: "lastLoginAt", icon: "clock", formatter: formatDateValue },
  ],
  salary: [
    { label: "Basic Salary", valueKey: "basicSalary", icon: "wallet", formatter: formatCurrencyValue },
    { label: "Pay Frequency", valueKey: "payFrequency", icon: "clock" },
  ],
  bank: [
    { label: "Bank Name", valueKey: "bankName", icon: "bank" },
    { label: "Account Number", valueKey: "accountNumber", icon: "hash" },
    { label: "IFSC Code", valueKey: "ifscCode", icon: "key" },
    { label: "PAN Number", valueKey: "panNumber", icon: "idCard" },
  ],
  address: [
    { label: "Address Line 1", valueKey: "addressLine1", icon: "mapPin" },
    { label: "Address Line 2", valueKey: "addressLine2", icon: "mapPin" },
    { label: "City", valueKey: "city", icon: "building" },
    { label: "State", valueKey: "state", icon: "globe" },
    { label: "PIN Code", valueKey: "pinCode", icon: "hash" },
    { label: "Country", valueKey: "country", icon: "globe" },
  ],
};

export function LogoBlock({ subtitle, logo }) {
  return (
    <div className="logo-block">
      <img
        src={logo}
        alt="Insta Team Logo"
        className="logo-image"
      />

      
    </div>
  );
}
export function LabeledInput({
  label,
  icon,
  type = "text",
  value,
  onChange,
  placeholder,
  trailingButton = null,
}) {
  return (
    <label className="form-field">
      <span>{label}</span>
      <div className="field-shell">
        <AppIcon name={icon} />
        <input
          type={type}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
        />
        {trailingButton}
      </div>
    </label>
  );
}

export function FeatureCard({ icon, title, description }) {
  return (
    <article className="feature-card">
      <div className="feature-icon">
        <AppIcon name={icon} />
      </div>
      <div>
        <strong>{title}</strong>
        <p>{description}</p>
      </div>
    </article>
  );
}

export function StatusBanner({ type, text, onClose }) {
  return (
    <div className={`status-banner ${type}`}>
      <span>{text}</span>
      {onClose ? (
        <button type="button" onClick={onClose}>
          <AppIcon name="close" />
        </button>
      ) : null}
    </div>
  );
}

export function EmployeePortalSidebar({
  items,
  activeView,
  onSelect,
  onLogout,
  notificationCounts = {},
}) {
  return (
    <aside className="employee-portal-sidebar">
      <div className="employee-portal-sidebar-top">
  <LogoBlock
    subtitle="HRMS & Management"
    logo="/instateamlogo2.png"
  />
</div>

      <nav className="employee-portal-nav">
        {items.map((item) => {
          const isActive = activeView === item.key;
          const hasUnread = Boolean(item.notificationCategory && notificationCounts[`${item.notificationCategory}Unread`]);

          return (
            <button
              key={item.key}
              type="button"
              className={`employee-portal-nav-item ${isActive ? "active" : ""}`}
              onClick={() => {
                if (item.key === "logout") {
                  onLogout();
                  return;
                }

                onSelect(item.key);
              }}
            >
              <AppIcon name={item.icon} />
              <span>{item.label}</span>
              {hasUnread ? <i className="employee-sidebar-unread-dot" aria-label="Unread updates" /> : null}
            </button>
          );
        })}
      </nav>
    </aside>
  );
}

export function EmployeePortalTopbar({
  employee,
  onOpenProfile,
  onOpenNotifications,
  onToggleSidebar,
  notificationCount = 0,
  searchText = "",
  onSearchChange = () => {},
  searchPlaceholder = "Search anything...",
  searchDisabled = false,
}) {
  const presenceMeta = getPresenceStatusMeta(resolveEmployeeProfileStatus(employee));

  return (
    <header className="employee-portal-topbar">
      <button type="button" className="employee-portal-icon-button" aria-label="Toggle sidebar" onClick={onToggleSidebar}>
        <AppIcon name="menu" />
      </button>

      <label className="employee-portal-search">
        <AppIcon name="search" />
        <input
          type="text"
          value={searchText}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder={searchPlaceholder}
          disabled={searchDisabled}
        />
      </label>

      <div className="employee-portal-topbar-actions">
        <button
          type="button"
          className="employee-portal-notification"
          aria-label="Notifications"
          onClick={onOpenNotifications}
        >
          <AppIcon name="bell" />
          {notificationCount ? <span>{notificationCount}</span> : null}
        </button>

        <button
          type="button"
          className="employee-portal-profile-trigger"
          onClick={onOpenProfile}
        >
          <EmployeeAvatar
            employee={employee}
            className="employee-portal-topbar-avatar"
            showPresence
          />
          <div className="employee-portal-profile-copy">
            <strong>{employee.fullName}</strong>
            <span>
              {formatFieldValue(employee.designation)} • {presenceMeta.label}
            </span>
          </div>
          <AppIcon name="chevronDown" />
        </button>
      </div>
    </header>
  );
}

export function EmployeeComingSoonView({ title }) {
  return (
    <section className="employee-portal-page">
      <div className="employee-page-heading">
        <h1>{title}</h1>
        <div className="employee-breadcrumb">
          <span>Dashboard</span>
          {title !== "Dashboard" ? (
            <>
              <span>›</span>
              <span>{title}</span>
            </>
          ) : null}
        </div>
      </div>

      <div className="employee-coming-soon-card">
        <div className="employee-coming-soon-icon">
          <AppIcon name="clock" />
        </div>
        <h2>Coming soon</h2>
        <p>This section is under preparation and will be available soon.</p>
      </div>
    </section>
  );
}

export function EmployeeProfileView({
  employee,
  profileTab,
  onProfileTabChange,
}) {
  const documents = getEmployeeDocuments(employee.documents);
  const fields = PROFILE_FIELDS[profileTab] || PROFILE_FIELDS.personal;

  return (
    <section className="employee-portal-page">
      <div className="employee-page-heading">
        <h1>My Profile</h1>
        <div className="employee-breadcrumb">
          <span>Dashboard</span>
          <span>›</span>
          <span>My Profile</span>
        </div>
      </div>

      <div className="employee-profile-layout">
        <div className="employee-profile-main">
          <article className="employee-profile-hero-card">
            <div className="employee-profile-hero-top">
              <EmployeeAvatar
                employee={employee}
                className="employee-profile-avatar"
                showPresence
              />

              <div className="employee-profile-hero-copy">
                <div className="employee-profile-title-row">
                  <h2>{formatFieldValue(employee.fullName)}</h2>
                  <span className={`employee-status-pill ${employee.status || "inactive"}`}>
                    {formatStatusLabel(employee.status)}
                  </span>
                </div>

                <p>
                  {formatFieldValue(employee.designation)}{" "}
                  <span className="employee-profile-divider">|</span>{" "}
                  {formatFieldValue(employee.department)}
                </p>
              </div>
            </div>

            <div className="employee-profile-meta-grid">
              <ProfileMetaItem icon="badge" label="Employee ID" value={employee.employeeId} />
              <ProfileMetaItem icon="atSign" label="Username" value={employee.username} />
              <ProfileMetaItem icon="mail" label="Email" value={employee.email} />
              <ProfileMetaItem icon="phone" label="Phone" value={employee.phoneNumber} />
              <ProfileMetaItem
                icon="calendar"
                label="Date of Joining"
                value={employee.dateOfJoining}
              />
              <ProfileMetaItem
                icon="briefcase"
                label="Employment Type"
                value={employee.employmentType}
              />
            </div>
          </article>

          <article className="employee-profile-details-card">
            <div className="employee-profile-tab-row">
              {PROFILE_TABS.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  className={`employee-profile-tab ${profileTab === tab.key ? "active" : ""}`}
                  onClick={() => onProfileTabChange(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="employee-profile-section">
              <h3>{PROFILE_TABS.find((tab) => tab.key === profileTab)?.label}</h3>
              <div className="employee-profile-detail-grid">
                {fields.map((field) => (
                  <ProfileDetailItem
                    key={field.label}
                    icon={field.icon}
                    label={field.label}
                    value={
                      field.formatter
                        ? field.formatter(employee[field.valueKey], employee)
                        : formatFieldValue(employee[field.valueKey])
                    }
                  />
                ))}
              </div>
            </div>
          </article>
        </div>

        <aside className="employee-profile-side">
          <article className="employee-profile-side-card">
            <div className="employee-side-heading">
              <h3>Documents</h3>
              <p>All documents uploaded by admin are available here.</p>
            </div>

            {documents.length ? (
              <div className="employee-profile-document-list">
                {documents.map(([key, file]) => (
                  <a
                    key={key}
                    className="employee-profile-document-item"
                    href={toAbsoluteUrl(file.url)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <div className="employee-profile-document-icon">
                      <AppIcon name="fileText" />
                    </div>

                    <div className="employee-profile-document-copy">
                      <strong>{formatDocumentLabel(key)}</strong>
                      <span>{file.originalName || "Uploaded document"}</span>
                    </div>

                    <span className="employee-document-badge">Uploaded</span>
                  </a>
                ))}
              </div>
            ) : (
              <div className="employee-profile-empty-state">
                <AppIcon name="documents" />
                <p>No documents have been uploaded yet.</p>
              </div>
            )}
          </article>

          <article className="employee-profile-side-card">
            <div className="employee-side-heading">
              <h3>Access Information</h3>
              <p>Your current company access details.</p>
            </div>

            <div className="employee-access-info-grid">
              <ProfileMetaItem
                icon="shield"
                label="Account Status"
                value={formatStatusLabel(employee.status)}
              />
              <ProfileMetaItem
                icon="clock"
                label="Last Login"
                value={formatDateValue(employee.lastLoginAt)}
              />
              <ProfileMetaItem
                icon="wallet"
                label="Basic Salary"
                value={formatCurrencyValue(employee.basicSalary)}
              />
              <ProfileMetaItem
                icon="bank"
                label="Bank Name"
                value={employee.bankName}
              />
            </div>
          </article>
        </aside>
      </div>
    </section>
  );
}

function ProfileMetaItem({ icon, label, value }) {
  return (
    <div className="employee-profile-meta-item">
      <div className="employee-profile-meta-icon">
        <AppIcon name={icon} />
      </div>
      <div>
        <span>{label}</span>
        <strong>{formatFieldValue(value)}</strong>
      </div>
    </div>
  );
}

function ProfileDetailItem({ icon, label, value }) {
  return (
    <div className="employee-profile-detail-item">
      <div className="employee-profile-detail-icon">
        <AppIcon name={icon} />
      </div>
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
      </div>
    </div>
  );
}

export function EmployeeAvatar({
  employee,
  className,
  showPresence = false,
  presenceStatus = "",
  fallbackLabel = "",
}) {
  const resolvedPresenceStatus = showPresence
    ? getPresenceStatusMeta(presenceStatus || resolveEmployeeProfileStatus(employee))
    : null;

  const avatarNode = employee?.profilePhoto?.url ? (
    <img
      src={toAbsoluteUrl(employee.profilePhoto.url)}
      alt={employee.fullName}
      className={className}
    />
  ) : (
    <div className={`${className} employee-avatar-fallback`}>
      {getInitials(employee?.fullName || fallbackLabel)}
    </div>
  );

  if (!showPresence) {
    return avatarNode;
  }

  return (
    <div className="employee-presence-avatar-wrap compact">
      {avatarNode}
      <span
        className={`employee-presence-badge compact ${resolvedPresenceStatus.className}`}
        title={resolvedPresenceStatus.label}
        aria-label={resolvedPresenceStatus.label}
      >
        {resolvedPresenceStatus.icon ? (
          <AppIcon name={resolvedPresenceStatus.icon} />
        ) : (
          <span className="employee-presence-dot" />
        )}
      </span>
    </div>
  );
}

export function AppIcon({ name }) {
  const props = {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.8",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    className: "app-icon",
  };

  switch (name) {
    case "mail":
      return (
        <svg {...props}>
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <path d="m4 7 8 6 8-6" />
        </svg>
      );
    case "user":
      return (
        <svg {...props}>
          <circle cx="12" cy="8" r="4" />
          <path d="M6 20a6 6 0 0 1 12 0" />
        </svg>
      );
    case "userCircle":
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="9" />
          <circle cx="12" cy="9" r="3" />
          <path d="M7 18a5 5 0 0 1 10 0" />
        </svg>
      );
    case "users":
      return (
        <svg {...props}>
          <path d="M16 19a4 4 0 0 0-8 0" />
          <circle cx="12" cy="10" r="3" />
          <path d="M22 19a4 4 0 0 0-3-3.87" />
          <path d="M5 15.13A4 4 0 0 0 2 19" />
        </svg>
      );
    case "lock":
      return (
        <svg {...props}>
          <rect x="4" y="11" width="16" height="10" rx="2" />
          <path d="M8 11V8a4 4 0 1 1 8 0v3" />
        </svg>
      );
    case "eye":
      return (
        <svg {...props}>
          <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12Z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      );
    case "eyeOff":
      return (
        <svg {...props}>
          <path d="m3 3 18 18" />
          <path d="M10.58 10.58A2 2 0 0 0 13.42 13.42" />
          <path d="M9.88 5.09A10.94 10.94 0 0 1 12 5c6.5 0 10 7 10 7a16.45 16.45 0 0 1-4.31 5.16" />
          <path d="M6.61 6.61A16.2 16.2 0 0 0 2 12s3.5 7 10 7a10.94 10.94 0 0 0 5.39-1.39" />
        </svg>
      );
    case "shield":
      return (
        <svg {...props}>
          <path d="M12 2 5 5v6c0 5 3.4 9.4 7 11 3.6-1.6 7-6 7-11V5z" />
        </svg>
      );
    case "shieldLock":
      return (
        <svg {...props}>
          <path d="M12 2 5 5v6c0 5 3.4 9.4 7 11 3.6-1.6 7-6 7-11V5z" />
          <rect x="9" y="11" width="6" height="5" rx="1" />
          <path d="M10 11V9a2 2 0 1 1 4 0v2" />
        </svg>
      );
    case "clock":
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 3" />
        </svg>
      );
    case "calendar":
      return (
        <svg {...props}>
          <rect x="3" y="5" width="18" height="16" rx="2" />
          <path d="M16 3v4M8 3v4M3 10h18" />
        </svg>
      );
    case "calendarCheck":
      return (
        <svg {...props}>
          <rect x="3" y="5" width="18" height="16" rx="2" />
          <path d="M16 3v4M8 3v4M3 10h18" />
          <path d="m9 15 2 2 4-4" />
        </svg>
      );
    case "calendarX":
      return (
        <svg {...props}>
          <rect x="3" y="5" width="18" height="16" rx="2" />
          <path d="M16 3v4M8 3v4M3 10h18" />
          <path d="m10 14 4 4M14 14l-4 4" />
        </svg>
      );
    case "documents":
    case "fileText":
      return (
        <svg {...props}>
          <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
          <path d="M14 3v6h6" />
          <path d="M8 13h8M8 17h6" />
        </svg>
      );
    case "close":
      return (
        <svg {...props}>
          <path d="M18 6 6 18M6 6l12 12" />
        </svg>
      );
    case "layoutDashboard":
      return (
        <svg {...props}>
          <rect x="3" y="3" width="8" height="8" rx="2" />
          <rect x="13" y="3" width="8" height="5" rx="2" />
          <rect x="13" y="10" width="8" height="11" rx="2" />
          <rect x="3" y="13" width="8" height="8" rx="2" />
        </svg>
      );
    case "checkSquare":
      return (
        <svg {...props}>
          <rect x="3" y="3" width="18" height="18" rx="3" />
          <path d="m8 12 3 3 5-6" />
        </svg>
      );
    case "plane":
      return (
        <svg {...props}>
          <path d="m2 12 19-8-5 18-4-7-10-3Z" />
          <path d="m12 15 9-11" />
        </svg>
      );
    case "wallet":
      return (
        <svg {...props}>
          <path d="M4 7h14a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2Z" />
          <path d="M16 12h4" />
          <path d="M4 7V5a2 2 0 0 1 2-2h11" />
        </svg>
      );
    case "download":
      return (
        <svg {...props}>
          <path d="M12 3v12" />
          <path d="m7 10 5 5 5-5" />
          <path d="M5 21h14" />
        </svg>
      );
    case "share":
      return (
        <svg {...props}>
          <circle cx="18" cy="5" r="3" />
          <circle cx="6" cy="12" r="3" />
          <circle cx="18" cy="19" r="3" />
          <path d="m8.6 10.5 6.8-4" />
          <path d="m8.6 13.5 6.8 4" />
        </svg>
      );
    case "star":
      return (
        <svg {...props}>
          <path d="m12 3 2.7 5.5 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.8 1-6.1-4.4-4.3 6.1-.9L12 3Z" />
        </svg>
      );
    case "target":
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="8" />
          <circle cx="12" cy="12" r="4" />
          <circle cx="12" cy="12" r="1" />
        </svg>
      );
    case "plus":
      return (
        <svg {...props}>
          <path d="M12 5v14M5 12h14" />
        </svg>
      );
    case "edit":
      return (
        <svg {...props}>
          <path d="M12 20h9" />
          <path d="m16.5 3.5 4 4L7 21H3v-4Z" />
        </svg>
      );
    case "check":
      return (
        <svg {...props}>
          <path d="m5 12 4 4L19 6" />
        </svg>
      );
    case "checkCheck":
      return (
        <svg {...props}>
          <path d="m3 12 4 4 3-3" />
          <path d="m10 12 4 4 7-8" />
        </svg>
      );
    case "checkCircle":
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="9" />
          <path d="m8 12 3 3 5-6" />
        </svg>
      );
    case "more":
      return (
        <svg {...props}>
          <circle cx="12" cy="5" r="1.5" fill="currentColor" />
          <circle cx="12" cy="12" r="1.5" fill="currentColor" />
          <circle cx="12" cy="19" r="1.5" fill="currentColor" />
        </svg>
      );
    case "logIn":
      return (
        <svg {...props}>
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
          <path d="M16 17l5-5-5-5" />
          <path d="M21 12H9" />
        </svg>
      );
    case "logOut":
      return (
        <svg {...props}>
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
          <path d="m16 17 5-5-5-5" />
          <path d="M21 12H9" />
        </svg>
      );
    case "megaphone":
      return (
        <svg {...props}>
          <path d="M3 11v2" />
          <path d="M7 10v4" />
          <path d="M7 12c8 0 10-4 14-6v12c-4-2-6-6-14-6Z" />
          <path d="M7 14 9 20" />
        </svg>
      );
      case "chevronRight":
      return (
        <svg {...props}>
          <path d="m9 6 6 6-6 6" />
        </svg>
      );
    case "chat":
    case "messageSquare":
      return (
        <svg {...props}>
          <path d="M21 15a2 2 0 0 1-2 2H8l-5 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2Z" />
        </svg>
      );
    case "reply":
      return (
        <svg {...props}>
          <path d="m9 17-5-5 5-5" />
          <path d="M4 12h9a6 6 0 0 1 6 6" />
        </svg>
      );
    case "forward":
      return (
        <svg {...props}>
          <path d="m15 17 5-5-5-5" />
          <path d="M20 12h-9a6 6 0 0 0-6 6" />
        </svg>
      );
    case "copy":
      return (
        <svg {...props}>
          <rect x="9" y="9" width="11" height="11" rx="2" />
          <rect x="4" y="4" width="11" height="11" rx="2" />
        </svg>
      );
    case "trash":
      return (
        <svg {...props}>
          <path d="M3 6h18" />
          <path d="M8 6V4h8v2" />
          <path d="M19 6l-1 14H6L5 6" />
          <path d="M10 11v5M14 11v5" />
        </svg>
      );
    case "video":
      return (
        <svg {...props}>
          <rect x="3" y="6" width="13" height="12" rx="2" />
          <path d="m16 10 5-3v10l-5-3" />
        </svg>
      );
    case "infoCircle":
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 10v5" />
          <path d="M12 7h.01" />
        </svg>
      );
    case "smile":
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="9" />
          <path d="M8 14s1.5 2 4 2 4-2 4-2" />
          <path d="M9 10h.01M15 10h.01" />
        </svg>
      );
    case "userPlus":
      return (
        <svg {...props}>
          <path d="M16 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2" />
          <circle cx="9.5" cy="7" r="3" />
          <path d="M19 8v6M16 11h6" />
        </svg>
      );
    case "userMinus":
      return (
        <svg {...props}>
          <path d="M16 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2" />
          <circle cx="9.5" cy="7" r="3" />
          <path d="M16 11h6" />
        </svg>
      );
    case "link":
      return (
        <svg {...props}>
          <path d="M10 13a5 5 0 0 1 0-7l1-1a5 5 0 0 1 7 7l-1 1" />
          <path d="M14 11a5 5 0 0 1 0 7l-1 1a5 5 0 1 1-7-7l1-1" />
        </svg>
      );
    case "ban":
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="9" />
          <path d="m6 6 12 12" />
        </svg>
      );
    case "alertCircle":
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 8v5" />
          <path d="M12 16h.01" />
        </svg>
      );
    case "barChart":
      return (
        <svg {...props}>
          <path d="M4 19V9" />
          <path d="M10 19V5" />
          <path d="M16 19v-7" />
          <path d="M22 19V3" />
        </svg>
      );
    case "settings":
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82L4.21 7.2a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h.08A1.65 1.65 0 0 0 10 3.25V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v.08A1.65 1.65 0 0 0 20.75 10H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
        </svg>
      );
    case "menu":
      return (
        <svg {...props}>
          <path d="M4 7h16M4 12h16M4 17h16" />
        </svg>
      );
    case "search":
      return (
        <svg {...props}>
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.5-3.5" />
        </svg>
      );
    case "bell":
      return (
        <svg {...props}>
          <path d="M15 17H5l1.4-1.4A2 2 0 0 0 7 14.17V10a5 5 0 1 1 10 0v4.17a2 2 0 0 0 .59 1.42L19 17h-4" />
          <path d="M10 20a2 2 0 0 0 4 0" />
        </svg>
      );
    case "chevronDown":
      return (
        <svg {...props}>
          <path d="m6 9 6 6 6-6" />
        </svg>
      );
    case "chevronLeft":
      return (
        <svg {...props}>
          <path d="m15 18-6-6 6-6" />
        </svg>
      );
      case "database":
      return (
        <svg {...props}>
          <ellipse cx="12" cy="5" rx="7" ry="3" />
          <path d="M5 5v6c0 1.7 3.1 3 7 3s7-1.3 7-3V5" />
          <path d="M5 11v6c0 1.7 3.1 3 7 3s7-1.3 7-3v-6" />
        </svg>
      );
    case "device":
      return (
        <svg {...props}>
          <rect x="3" y="5" width="14" height="10" rx="2" />
          <rect x="14" y="11" width="7" height="8" rx="1.5" />
          <path d="M8 19h5" />
        </svg>
      );
    case "phoneDevice":
      return (
        <svg {...props}>
          <rect x="7" y="2" width="10" height="20" rx="2" />
          <path d="M11 18h2" />
        </svg>
      );
    case "palette":
      return (
        <svg {...props}>
          <path d="M12 3a9 9 0 0 0 0 18h1.4a2 2 0 0 0 1.4-3.4 1.4 1.4 0 0 1 1-2.4H17a4 4 0 0 0 4-4C21 6.6 17 3 12 3Z" />
          <circle cx="8" cy="10" r=".7" />
          <circle cx="11" cy="7.5" r=".7" />
          <circle cx="15" cy="8.5" r=".7" />
        </svg>
      );
    case "cloud":
      return (
        <svg {...props}>
          <path d="M17.5 18H7a4 4 0 1 1 .7-7.94A5 5 0 0 1 17 9a4.5 4.5 0 0 1 .5 9Z" />
        </svg>
      );
    case "sliders":
      return (
        <svg {...props}>
          <path d="M4 7h16M4 12h16M4 17h16" />
          <circle cx="9" cy="7" r="2" />
          <circle cx="15" cy="12" r="2" />
          <circle cx="11" cy="17" r="2" />
        </svg>
      );
    case "puzzle":
      return (
        <svg {...props}>
          <path d="M8 3h4v4a2 2 0 1 0 4 0V3h3v6h-4a2 2 0 1 0 0 4h4v8h-6v-4a2 2 0 1 0-4 0v4H3v-6h4a2 2 0 1 0 0-4H3V8h5Z" />
        </svg>
      );
    case "pin":
      return (
        <svg {...props}>
          <path d="m14 4 6 6-4 1-4 7-2-2 7-4 1-4-6-6Z" />
          <path d="m8 16-4 4" />
        </svg>
      );
    case "arrowLeft":
      return (
        <svg {...props}>
          <path d="M19 12H5" />
          <path d="m12 5-7 7 7 7" />
        </svg>
      );
      case "xCircle":
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="9" />
          <path d="m9 9 6 6M15 9l-6 6" />
        </svg>
      );
    case "clipboard":
      return (
        <svg {...props}>
          <rect x="5" y="4" width="14" height="17" rx="2" />
          <path d="M9 4a3 3 0 0 1 6 0" />
          <path d="M9 10h6M9 14h6M9 18h4" />
        </svg>
      );
    case "paperclip":
      return (
        <svg {...props}>
          <path d="m21 11-8.5 8.5a5 5 0 0 1-7.1-7.1L14 3.8a3.2 3.2 0 0 1 4.5 4.5L9.9 16.9a1.5 1.5 0 0 1-2.1-2.1L16 6.6" />
        </svg>
      );
    case "send":
      return (
        <svg {...props}>
          <path d="m22 2-7 20-4-9-9-4 20-7Z" />
          <path d="M22 2 11 13" />
        </svg>
      );
    case "moreVertical":
      return (
        <svg {...props}>
          <circle cx="12" cy="5" r="1" />
          <circle cx="12" cy="12" r="1" />
          <circle cx="12" cy="19" r="1" />
        </svg>
      );
    case "phone":
      return (
        <svg {...props}>
          <path d="M22 16.9v3a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07A19.5 19.5 0 0 1 5.18 12.8 19.8 19.8 0 0 1 2.1 4.08 2 2 0 0 1 4.09 1.9h3a2 2 0 0 1 2 1.72l.35 2.43a2 2 0 0 1-.57 1.73l-1.2 1.2a16 16 0 0 0 6.9 6.9l1.2-1.2a2 2 0 0 1 1.73-.57l2.43.35a2 2 0 0 1 1.72 2Z" />
        </svg>
      );
    case "heart":
      return (
        <svg {...props}>
          <path d="m12 21-1.4-1.27C5.4 15 2 11.9 2 8.1 2 5 4.4 3 7.3 3c1.7 0 3.4.8 4.7 2.1C13.3 3.8 15 3 16.7 3 19.6 3 22 5 22 8.1c0 3.8-3.4 6.9-8.6 11.63Z" />
        </svg>
      );
    case "badge":
      return (
        <svg {...props}>
          <rect x="5" y="3" width="14" height="18" rx="2" />
          <path d="M9 7h6M8 12h8M10 16h4" />
        </svg>
      );
    case "building":
      return (
        <svg {...props}>
          <path d="M3 21h18" />
          <path d="M5 21V6a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v15" />
          <path d="M13 21V10a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v11" />
          <path d="M8 9h2M8 13h2M8 17h2M16 13h1M16 17h1" />
        </svg>
      );
    case "briefcase":
      return (
        <svg {...props}>
          <rect x="3" y="7" width="18" height="12" rx="2" />
          <path d="M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
          <path d="M3 12h18" />
        </svg>
      );
    case "userCog":
      return (
        <svg {...props}>
          <circle cx="10" cy="8" r="3" />
          <path d="M4 19a6 6 0 0 1 12 0" />
          <path d="m18 8 .8.4.9-.3.7.7-.3.9.4.8-.4.8.3.9-.7.7-.9-.3-.8.4-.8-.4-.9.3-.7-.7.3-.9-.4-.8.4-.8-.3-.9.7-.7.9.3Z" />
        </svg>
      );
    case "layers":
      return (
        <svg {...props}>
          <path d="m12 3 9 5-9 5-9-5 9-5Z" />
          <path d="m3 12 9 5 9-5" />
          <path d="m3 16 9 5 9-5" />
        </svg>
      );
    case "bank":
      return (
        <svg {...props}>
          <path d="m3 10 9-5 9 5" />
          <path d="M5 10v8M9 10v8M15 10v8M19 10v8" />
          <path d="M3 21h18" />
        </svg>
      );
    case "hash":
      return (
        <svg {...props}>
          <path d="M4 9h16M4 15h16M9 4 7 20M17 4l-2 16" />
        </svg>
      );
    case "key":
      return (
        <svg {...props}>
          <circle cx="7.5" cy="15.5" r="3.5" />
          <path d="m11 13 8-8 2 2-8 8" />
          <path d="M17 7l2 2" />
        </svg>
      );
    case "idCard":
      return (
        <svg {...props}>
          <rect x="3" y="6" width="18" height="12" rx="2" />
          <circle cx="8" cy="12" r="2" />
          <path d="M13 10h5M13 14h5" />
        </svg>
      );
    case "mapPin":
      return (
        <svg {...props}>
          <path d="M12 21s6-5.4 6-11a6 6 0 1 0-12 0c0 5.6 6 11 6 11Z" />
          <circle cx="12" cy="10" r="2.5" />
        </svg>
      );
    case "globe":
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="9" />
          <path d="M3 12h18M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18" />
        </svg>
      );
    case "atSign":
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="4" />
          <path d="M16 12v1a2 2 0 1 0 4 0 8 8 0 1 0-2.34 5.66" />
        </svg>
      );
    default:
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="9" />
        </svg>
      );
  }
}
